(() => {
  const STORAGE_KEY = 'stonylab_recent_searches';
  const MAX_ITEMS = 8;
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const FORM_SELECTOR =
    '[mobile_search_form] form[role="search"], .header__icons .desktop-search form[role="search"]';

  const sanitizeHistory = (value) =>
    Array.isArray(value)
      ? value.filter((item) => typeof item === 'string' && item.trim()).slice(0, MAX_ITEMS)
      : [];

  const readLocalHistory = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return sanitizeHistory(value);
    } catch (error) {
      return [];
    }
  };

  const writeLocalHistory = (items) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // Search still works when browser storage is unavailable.
    }
  };

  const readCookieHistory = () => {
    const prefix = `${STORAGE_KEY}=`;
    const cookie = document.cookie.split('; ').find((item) => item.startsWith(prefix));
    if (!cookie) return null;

    try {
      return sanitizeHistory(JSON.parse(decodeURIComponent(cookie.slice(prefix.length))));
    } catch (error) {
      return null;
    }
  };

  const writeCookieHistory = (items) => {
    const hostname = window.location.hostname.toLocaleLowerCase();
    const domain =
      hostname === 'stonylab.com' || hostname.endsWith('.stonylab.com') ? '; Domain=.stonylab.com' : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(
      JSON.stringify(items),
    )}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${domain}${secure}`;
  };

  const readHistory = () => {
    const cookieHistory = readCookieHistory();
    if (cookieHistory !== null) {
      writeLocalHistory(cookieHistory);
      return cookieHistory;
    }

    const localHistory = readLocalHistory();
    writeCookieHistory(localHistory);
    return localHistory;
  };

  const writeHistory = (items) => {
    const history = sanitizeHistory(items);
    writeLocalHistory(history);
    writeCookieHistory(history);
  };

  const addSearch = (query) => {
    const term = query.trim();
    if (!term) return;

    const normalizedTerm = term.toLocaleLowerCase();
    const items = readHistory().filter((item) => item.toLocaleLowerCase() !== normalizedTerm);
    writeHistory([term, ...items]);
  };

  const createIcon = () => {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    const use = document.createElementNS(namespace, 'use');

    icon.classList.add('icon', 'icon-close');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    use.setAttribute('href', '#icon-close');
    icon.appendChild(use);

    return icon;
  };

  const createPanel = (id) => {
    const panel = document.createElement('div');
    const heading = document.createElement('p');
    const list = document.createElement('ul');

    panel.id = id;
    panel.className = 'header-search-history';
    panel.hidden = true;
    panel.setAttribute('aria-labelledby', `${id}-heading`);

    heading.id = `${id}-heading`;
    heading.className = 'header-search-history__heading';
    heading.textContent = 'Recent Searches';

    list.className = 'header-search-history__list';
    list.setAttribute('role', 'list');

    panel.append(heading, list);
    return panel;
  };

  const initializeForm = (form, index) => {
    if (form.dataset.searchHistoryInitialized === 'true') return;

    const input = form.querySelector('input[type="search"][name="q"]');
    const field = form.querySelector('.field');
    if (!input || !field) return;

    form.dataset.searchHistoryInitialized = 'true';
    form.classList.add('header-search-history-form');

    const panelId = `HeaderSearchHistory-${index}`;
    const panel = createPanel(panelId);
    const list = panel.querySelector('.header-search-history__list');
    field.insertAdjacentElement('afterend', panel);

    const close = () => {
      panel.hidden = true;
    };

    const render = () => {
      const items = readHistory();
      list.replaceChildren();

      items.forEach((term) => {
        const item = document.createElement('li');
        const searchButton = document.createElement('button');
        const removeButton = document.createElement('button');

        item.className = 'header-search-history__item';

        searchButton.type = 'button';
        searchButton.className = 'header-search-history__term';
        searchButton.textContent = term;
        searchButton.addEventListener('click', () => {
          input.value = term;
          addSearch(term);
          close();
          input.dispatchEvent(new Event('input', { bubbles: true }));

          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });

        removeButton.type = 'button';
        removeButton.className = 'header-search-history__remove';
        removeButton.title = 'Remove';
        removeButton.setAttribute('aria-label', `Remove ${term} from recent searches`);
        removeButton.appendChild(createIcon());
        removeButton.addEventListener('click', (event) => {
          event.stopPropagation();
          const normalizedTerm = term.toLocaleLowerCase();
          writeHistory(readHistory().filter((entry) => entry.toLocaleLowerCase() !== normalizedTerm));
          render();
        });

        item.append(searchButton, removeButton);
        list.appendChild(item);
      });

      panel.hidden = items.length === 0 || input.value.trim().length > 0;
    };

    const openIfEmpty = () => {
      if (!input.value.trim()) render();
    };

    input.addEventListener('focus', openIfEmpty);
    input.addEventListener('click', openIfEmpty);
    input.addEventListener('input', () => {
      if (input.value.trim()) {
        close();
      } else {
        render();
      }
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        close();
        event.stopPropagation();
      }
    });

    form.addEventListener('submit', () => addSearch(input.value));
    form.addEventListener('reset', () => window.setTimeout(openIfEmpty));

    document.addEventListener('pointerdown', (event) => {
      if (!form.contains(event.target)) close();
    });

    const searchPath = new URL(form.action, window.location.origin).pathname;
    if (window.location.pathname === searchPath) {
      addSearch(new URLSearchParams(window.location.search).get('q') || '');
    }
  };

  const initializeSearchHistory = (scope = document) => {
    const forms = scope.matches?.(FORM_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(FORM_SELECTOR));

    forms.forEach((form, index) => initializeForm(form, index));
  };

  readHistory();
  initializeSearchHistory();

  document.addEventListener('shopify:section:load', (event) => initializeSearchHistory(event.target));
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;

    document.querySelectorAll(`${FORM_SELECTOR}`).forEach((form) => {
      const input = form.querySelector('input[type="search"][name="q"]');
      const panel = form.querySelector('.header-search-history');
      if (input === document.activeElement && !input.value.trim() && panel) input.click();
    });
  });
})();
